import type { PackageItemFormData } from "@src/common/components/PackageDimensionsForm/PackageDimensionsForm";
import { PackageSize } from "@api/graphql/generated/generated-types";
import { roundToOneDecimal } from "@src/common/lib/NumberUtils";
import { isDimensionless } from "./packageTypeDefaults";

/**
 * A row's piece count, with the one-piece default that rows written before quantity existed
 * carry. Mirrors the backend `PackageItem.concreteQuantity`.
 *
 * Consumer-defined input shape, so any object that structurally has an optional `quantity`
 * flows through without casting.
 */
export function concretePackageItemQuantity(item: {
  quantity?: number | null;
}): number {
  return item.quantity ?? 1;
}

/**
 * Whether an empty quantity still stands for one piece. A lone base row with no dimensions
 * and no typed quantity is not a package item yet — shipment scalars carry what it holds —
 * so neither the quantity placeholder nor the heading Total Quantity advertise the default.
 * Any dimension, a typed quantity, or a second row makes it a real item, and empty reads as 1.
 */
export function assumesDefaultQuantity(
  item: PackageItemFormData,
  isLoneBaseRow: boolean,
): boolean {
  if (!isLoneBaseRow) return true;
  return item.quantity != null || !isDimensionless(item);
}

/** Total physical pieces across rows: the number `numPackages` has to cover. */
export function countPackageItemPieces(
  packageItems: { quantity?: number | null }[] | null | undefined,
): number {
  if (!packageItems) return 0;
  return packageItems.reduce(
    (total, item) => total + concretePackageItemQuantity(item),
    0,
  );
}

interface HydrateBasePackageFromShipmentArgs {
  packageItems: PackageItemFormData[] | null | undefined;
  numPackages: number | null | undefined;
  packageWeightInPounds: number | null | undefined;
  packageTypeId: string | null | undefined;
}

/**
 * Edit surfaces always need the same base row as Create Order. Existing itemized
 * data wins; scalar-only legacy data is represented by one dimensionless row.
 */
export function hydrateBasePackageFromShipment({
  packageItems,
  numPackages,
  packageWeightInPounds,
  packageTypeId,
}: HydrateBasePackageFromShipmentArgs): PackageItemFormData[] {
  if (packageItems && packageItems.length > 0) return packageItems;

  const quantity: number | undefined =
    numPackages != null && numPackages > 0 ? numPackages : undefined;
  const basePackage: PackageItemFormData = {};
  if (quantity != null) basePackage.quantity = quantity;
  if (packageWeightInPounds != null) {
    basePackage.weight =
      quantity != null ? packageWeightInPounds / quantity : packageWeightInPounds;
  }
  if (packageTypeId != null) basePackage.packageTypeId = packageTypeId;
  return [basePackage];
}

/**
 * Shipment `numPackages` while typing and on submit. Absent on an untouched lone
 * base row so the heading never claims a piece count nobody entered.
 */
export function derivedNumPackages(
  packages: PackageItemFormData[] | undefined,
  hasBasePackage?: boolean,
): number | null {
  if (!packages || packages.length === 0) return null;
  const isLoneBaseRow = !!hasBasePackage && packages.length === 1;
  if (!assumesDefaultQuantity(packages[0], isLoneBaseRow)) return null;
  return countPackageItemPieces(packages);
}

/**
 * Shipment `packageWeightInPounds`: per-piece weight × quantity, summed.
 * Rounded to one decimal so 0.1 + 0.2 submits as 0.3, matching the heading.
 */
export function totalPackageItemWeight(
  packages: PackageItemFormData[] | undefined,
): number | null {
  if (!packages || packages.length === 0) return null;
  const weighedPackages = packages.filter(
    (pkg): pkg is PackageItemFormData & { weight: number } => pkg.weight != null,
  );
  if (weighedPackages.length === 0) return null;
  const total: number = weighedPackages.reduce(
    (sum, pkg) => sum + pkg.weight * concretePackageItemQuantity(pkg),
    0,
  );
  return roundToOneDecimal(total);
}

/**
 * Shipment `packageTypeId` is a write-back of the item rows' Type when Type is shown:
 * the single row's type, or — with two or more rows — the one type every row explicitly
 * agrees on. A row left as None (no override, `packageTypeId == null`) is not "the same
 * type as the others" — an item's effective type coalesces to whatever the shipment ends
 * up holding, so treating a None row as agreement would let the *other* rows silently
 * dictate that row's price type instead of leaving it on inherit. So a None row forces
 * null just as a disagreeing type does: only when every row explicitly carries the one
 * shared type is it written back. Hidden Type, no rows with a type, any row on None, or
 * two+ disagreeing types → null.
 *
 * This backfill matters beyond display: base-price pricing rules match on the
 * *shipment's* packageTypeId only (item-level overrides are pricing-relevant for
 * surcharges/skid, not the base rate — see docs/outlines/2026-08-08_package-type-per-package.md).
 * A multi-row order where every row is the same real type must still write that type to
 * the shipment, or its base rate silently stops matching (CUBE-PROD, order #47125).
 */
export function derivedShipmentPackageTypeId(
  packages: PackageItemFormData[] | undefined,
  typeOrSizeMode: "type" | "size" | "none",
): string | null {
  if (typeOrSizeMode !== "type") return null;
  if (!packages || packages.length === 0) return null;
  if (packages.length === 1) return packages[0].packageTypeId ?? null;

  const packageTypeIds: (string | null | undefined)[] = packages.map(
    (packageItem) => packageItem.packageTypeId,
  );
  const everyRowHasAnExplicitType: boolean = packageTypeIds.every(
    (packageTypeId) => packageTypeId != null,
  );
  if (!everyRowHasAnExplicitType) return null;

  const distinctTypeIds: string[] = Array.from(new Set(packageTypeIds as string[]));
  return distinctTypeIds.length === 1 ? distinctTypeIds[0] : null;
}

/**
 * Create-order submit/preview: a lone dimensionless row is not a package item —
 * its quantity, weight, and Type already live on the shipment scalars. Two or
 * more rows are always itemized, even with no dimensions.
 */
export function packageItemsForCreateSubmit(
  packageItems: PackageItemFormData[] | undefined,
): PackageItemFormData[] | undefined {
  if (!packageItems || packageItems.length === 0) return undefined;
  if (packageItems.length === 1 && isDimensionless(packageItems[0])) {
    return undefined;
  }
  return packageItems;
}

/** Size lives on the shipment, not the item. Select may store an object or a one-item array. */
export function shipmentPackageSizeId(value: unknown): PackageSize | undefined {
  const id: string | undefined = rawPackageSizeId(value);
  if (id == null || !isPackageSize(id)) return undefined;
  return id;
}

function isPackageSize(id: string): id is PackageSize {
  return (Object.values(PackageSize) as string[]).includes(id);
}

function rawPackageSizeId(value: unknown): string | undefined {
  if (value == null || value === "") return undefined;
  if (Array.isArray(value)) {
    const first: unknown = value[0];
    return rawPackageSizeId(first);
  }
  if (typeof value === "string") return value;
  if (typeof value === "object" && value !== null && "id" in value) {
    const id: unknown = (value as { id?: unknown }).id;
    return typeof id === "string" ? id : undefined;
  }
  return undefined;
}

/**
 * Drop package-dimension rows that carry no data at all. Returns undefined when
 * nothing valid remains, so callers can treat "no packages" as "no change".
 * Shared by the order-edit and quote-edit save paths so both send the same shape.
 *
 * A typed quantity counts as data on its own — "5 envelopes, dimensions unknown" is a real
 * row. New rows start with no quantity, so an untouched row is still dropped.
 */
export function filterValidPackageItems(
  packageItems: PackageItemFormData[] | undefined,
): PackageItemFormData[] | undefined {
  if (!packageItems || packageItems.length === 0) return undefined;

  const validItems = packageItems.filter(
    (item) =>
      item.width != null ||
      item.height != null ||
      item.length != null ||
      item.weight != null ||
      item.packageTypeId != null ||
      item.quantity != null,
  );

  return validItems.length > 0 ? validItems : undefined;
}
